import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";

const CORAL = "#E85D3A";
const LS_LAST = "feedback_last_shown";
const LS_NEVER = "feedback_never_show";
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

// Module-level: persists across re-mounts within the same session.
let feedbackShownThisSession = false;

type Rating = "love" | "okay" | "not_great";

function shouldShow(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (localStorage.getItem(LS_NEVER) === "true") return false;
    if (feedbackShownThisSession) return false;
    const last = localStorage.getItem(LS_LAST);
    if (!last) return true;
    const ts = Number(last);
    if (!Number.isFinite(ts)) return true;
    return Date.now() - ts > THIRTY_DAYS;
  } catch {
    return false;
  }
}

function markShown() {
  try {
    localStorage.setItem(LS_LAST, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function FeedbackWidget({
  outfitId,
  analysisMode,
  userId,
  lookbookReady,
}: {
  outfitId: string | null;
  analysisMode: "find_look" | "style_item" | null;
  userId: string | null;
  lookbookReady: boolean;
}) {
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!lookbookReady) return;
    if (!shouldShow()) return;
    const t = setTimeout(() => {
      if (!shouldShow()) return;
      feedbackShownThisSession = true;
      setVisible(true);
      // next frame: slide in
      requestAnimationFrame(() => setAnimateIn(true));
    }, 8000);
    return () => clearTimeout(t);
  }, [lookbookReady]);

  const dismiss = () => {
    setAnimateIn(false);
    setTimeout(() => setVisible(false), 300);
  };

  const logFeedback = async (rating: Rating, commentText: string | null) => {
    try {
      const guestId =
        !userId && typeof window !== "undefined"
          ? localStorage.getItem("guest_session_id")
          : null;
      await supabase.from("feedback").insert({
        user_id: userId ?? null,
        guest_session_id: userId ? null : guestId,
        outfit_id: outfitId,
        rating,
        comment: commentText,
        analysis_mode: analysisMode,
      } as never);
    } catch (err) {
      console.error("Feedback log error:", err);
    }
    trackEvent("feedback_submitted", { rating, has_comment: !!commentText });
  };

  const handleRating = (rating: Rating) => {
    if (rating === "not_great") {
      setExpanded(true);
      return;
    }
    markShown();
    void logFeedback(rating, null);
    if (rating === "love") toast.success("Thanks! 🙌", { duration: 1500, position: "top-center" });
    else toast("Thanks for the feedback!", { duration: 1500, position: "top-center" });
    dismiss();
  };

  const handleSend = async () => {
    setSubmitting(true);
    markShown();
    await logFeedback("not_great", comment.trim() || null);
    toast("Thanks — we'll improve!", { duration: 1500, position: "top-center" });
    setSubmitting(false);
    dismiss();
  };

  const handleSkip = () => {
    markShown();
    void logFeedback("not_great", null);
    dismiss();
  };

  const handleClose = () => {
    markShown();
    dismiss();
  };

  const handleNeverAsk = () => {
    try {
      localStorage.setItem(LS_NEVER, "true");
    } catch {
      /* ignore */
    }
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 w-full max-w-md px-3 z-40 pointer-events-none"
      style={{ bottom: 88 }}
    >
      <div
        className="pointer-events-auto bg-white rounded-2xl shadow-lg border"
        style={{
          borderColor: "rgba(0,0,0,0.06)",
          padding: "12px 16px",
          borderTopWidth: 1,
          borderTopColor: "var(--color-border, rgba(0,0,0,0.08))",
          transform: animateIn ? "translateY(0)" : "translateY(120%)",
          opacity: animateIn ? 1 : 0,
          transition: "transform 0.3s ease, opacity 0.3s ease",
        }}
      >
        {!expanded ? (
          <div>
            <div className="flex items-center gap-3">
              <span style={{ fontSize: 13, color: "#6b7280" }} className="flex-1">
                How are these results?
              </span>
              {(["love", "okay", "not_great"] as const).map((r) => {
                const emoji = r === "love" ? "😍" : r === "okay" ? "😐" : "😕";
                return (
                  <button
                    key={r}
                    onClick={() => handleRating(r)}
                    aria-label={r}
                    style={{ width: 44, height: 44, fontSize: 26, lineHeight: 1 }}
                    className="flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition"
                  >
                    {emoji}
                  </button>
                );
              })}
              <button
                onClick={handleClose}
                aria-label="Dismiss"
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X size={16} />
              </button>
            </div>
            <div className="mt-1 text-right">
              <button
                onClick={handleNeverAsk}
                style={{ fontSize: 12, color: "#9ca3af" }}
                className="hover:underline"
              >
                Don't ask again
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <input
              autoFocus
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What could be better?"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
              maxLength={500}
            />
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleSkip}
                disabled={submitting}
                style={{ fontSize: 13, color: "#9ca3af" }}
                className="hover:underline"
              >
                Skip
              </button>
              <button
                onClick={handleSend}
                disabled={submitting}
                style={{ background: CORAL }}
                className="text-white text-sm font-semibold px-4 py-2 rounded-full disabled:opacity-60"
              >
                Send
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
